import { getFileActions, useFileStore } from "../stores/fileStore";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { usePanelStore } from "../stores/panelStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { getGraspActions } from "../stores/graspStore";
import { getLibraryAssetByKey, getLibraryAssetKey } from "./libraryCatalog";
import { resolveCritterAssetsUrl } from "./assetPaths";
import { pickShallowAttachmentSiteUuidForWorldRoot } from "./libraryGripperAttachTargets";
import { loadLibraryAsset } from "./libraryAssetLoader";
import { loadXmlSourceScene } from "./xmlSourceLoader";
import { shouldBlockSceneAuthoringMutation } from "./physicsGuards";
import { CAMERA_PRESET_HANDLERS } from "./scene-recipes/cameraPresets";
import { cubeAsset, pandaAsset, trossenAsset, trayAsset } from "./scene-recipes/libraryAssets";
import {
  manipulationRecipeUiMetadata,
  manipulationRecipes,
} from "./scene-recipes/recipes/manipulation";
import {
  environmentRecipeUiMetadata,
  environmentRecipes,
} from "./scene-recipes/recipes/environment";
import { logger } from "./logger";
import { startSimulationFromCurrentFiles, stopSimulation } from "../physics/mujocoBridge";

const RECIPE_UI_METADATA = {
  ...manipulationRecipeUiMetadata,
  ...environmentRecipeUiMetadata,
};

const getRecipeUiMetadata = (recipe) => {
  const baseMetadata = RECIPE_UI_METADATA[recipe.id] || {
    groupId: "other",
    groupLabel: "Other",
    scenarioId: recipe.id,
    scenarioLabel: recipe.label,
  };
  const defaultRobotThumbnail = baseMetadata.robotId
    ? resolveCritterAssetsUrl(`/robots/${baseMetadata.robotId}/thumbnail.png`)
    : "";
  const thumbnailPath =
    (typeof baseMetadata.thumbnailPath === "string" && baseMetadata.thumbnailPath.trim()) ||
    defaultRobotThumbnail;
  return {
    groupId: baseMetadata.groupId,
    groupLabel: baseMetadata.groupLabel,
    scenarioId: baseMetadata.scenarioId,
    scenarioLabel: baseMetadata.scenarioLabel,
    robotId: baseMetadata.robotId || "",
    robotLabel: baseMetadata.robotLabel || "",
    thumbnailPath,
  };
};

/** @param {string} recipeId */
export const getSceneRecipeUiMetadataById = (recipeId) => {
  const id = String(recipeId || "").trim();
  if (!id) return null;
  const recipe = SCENE_RECIPES.find((entry) => entry.id === id);
  if (!recipe) return null;
  return getRecipeUiMetadata(recipe);
};

const clearSceneForRecipeLoad = () => {
  const sceneActions = getSceneActions();
  sceneActions.resetScene();
  getFileActions().clearAllKeyframes();
  getFileActions().setFileMap({});
  useFileStore.setState({
    xmlFile: null,
    referencedFiles: [],
    missingFiles: [],
    currentLoadingFolder: undefined,
  });
  const selectionActions = useSelectionStore.getState();
  selectionActions.clearAllSelections();
  selectionActions.clearAllHovers();
  try {
    usePanelStore.getState().actions.setExpandedBodies({});
  } catch (error) {
    logger.warn("[sceneRecipes] Failed to reset expanded bodies for recipe load.", error);
  }
  try {
    getGraspActions().clearAllGrasps();
  } catch (error) {
    logger.warn("[sceneRecipes] Failed to clear grasps for recipe load.", error);
  }
};

const stopPhysicsForRecipeLoad = async () => {
  const wasPhysicsRunning = usePhysicsStore.getState().isRunning;
  if (!wasPhysicsRunning) return false;
  await stopSimulation({ restoreScene: false });
  return true;
};

const restartPhysicsAfterRecipeLoad = async (wasPhysicsRunning) => {
  if (!wasPhysicsRunning) return;
  await new Promise((resolve) => setTimeout(resolve, 50));
  await startSimulationFromCurrentFiles({ preserveActuatorControls: true });
};

const applyStepKeyframeIfNeeded = async (step, loadResult) => {
  if (!step?.applyKeyframe || !loadResult?.ownerLabel) return;
  await getFileActions().handleKeyframeSelect(
    `${loadResult.ownerLabel} :: ${step.applyKeyframe}`,
    loadResult.rootBodyUuid || undefined,
    { preserveWorldRootPose: false }
  );
};

const attachRecipeGripperToRobot = (gripperRootBodyUuid, robotRootBodyUuid) => {
  if (!gripperRootBodyUuid || !robotRootBodyUuid) return;
  const sceneActions = getSceneActions();
  const bodies = getSceneState().bodies || {};
  const allSites = sceneActions.findAttachmentSites("attachment_site") || [];
  const gripperSiteUuid = pickShallowAttachmentSiteUuidForWorldRoot(
    bodies,
    allSites,
    gripperRootBodyUuid,
    "attachment_site"
  );
  const robotSiteUuid = pickShallowAttachmentSiteUuidForWorldRoot(
    bodies,
    allSites,
    robotRootBodyUuid,
    null
  );
  const attached = sceneActions.attachBySites(
    gripperRootBodyUuid,
    robotRootBodyUuid,
    "attachment_site",
    gripperSiteUuid || undefined,
    robotSiteUuid || undefined
  );
  if (!attached) {
    logger.warn("[sceneRecipes] Failed to attach gripper recipe step to robot.");
  }
};

export const loadLibraryRecipeStep = async (step, extra = {}) => {
  if (shouldBlockSceneAuthoringMutation("loadLibraryRecipeStep")) return null;
  const asset = step.libraryAsset ?? getLibraryAssetByKey(step.assetKey);
  if (!asset) {
    throw new Error(
      step.assetKey != null
        ? `Unknown library asset for recipe step: ${step.assetKey}`
        : "Recipe step missing library asset (provide assetKey or libraryAsset)"
    );
  }
  const loadResult = await loadLibraryAsset(asset, {
    restartPhysics: false,
    selectLoadedBody: false,
    transformOverride: step.transform || null,
    preserveSpawnKeyframeTransform: false,
    ...extra,
  });
  await applyStepKeyframeIfNeeded(step, loadResult);
  return loadResult;
};

const loadRecipe = async (recipe, opts = {}) => {
  if (shouldBlockSceneAuthoringMutation("loadRecipe")) return;
  const { onProgress } = opts;
  const prog = (p) => {
    if (typeof onProgress === "function") onProgress(Math.min(1, Math.max(0, p)));
  };

  const wasPhysicsRunning = await stopPhysicsForRecipeLoad();
  clearSceneForRecipeLoad();

  try {
    if (recipe.kind === "externalXml") {
      prog(0.05);
      await loadXmlSourceScene({
        xmlPath: recipe.xmlPath,
        xmlFilename: recipe.xmlFilename,
        worldBodyNameBase: recipe.worldBodyNameBase,
      });
      prog(0.92);
    } else {
      const steps = recipe.steps || [];
      const n = Math.max(1, steps.length);
      let lastRobotRootBodyUuid = null;
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const asset = step.libraryAsset ?? getLibraryAssetByKey(step.assetKey);
        const loadResult = await loadLibraryRecipeStep(step, {
          onProgress: (t) => prog(0.06 + 0.82 * ((i + t) / n)),
        });
        if (asset?.folder === "robots" && loadResult?.rootBodyUuid) {
          lastRobotRootBodyUuid = loadResult.rootBodyUuid;
        } else if (asset?.category === "attachment" && loadResult?.rootBodyUuid) {
          attachRecipeGripperToRobot(loadResult.rootBodyUuid, lastRobotRootBodyUuid);
        }
      }
      getFileActions().clearAllKeyframes();
      prog(0.9);
    }

    if (recipe.cameraPreset && CAMERA_PRESET_HANDLERS[recipe.cameraPreset]) {
      CAMERA_PRESET_HANDLERS[recipe.cameraPreset]();
    }
    prog(0.95);

    const latestWorldBody = [...(getSceneState().worldBodies || [])].pop();
    const latestRootUuid =
      typeof latestWorldBody === "string" ? latestWorldBody : latestWorldBody?.uuid;
    if (latestRootUuid) {
      const selectionActions = useSelectionStore.getState();
      selectionActions.setLastSelectionSource("presetScene");
      selectionActions.selectBody(null);
      selectionActions.selectBody(latestRootUuid);
    }
    prog(1);
  } finally {
    await restartPhysicsAfterRecipeLoad(wasPhysicsRunning);
  }
};

export const SCENE_RECIPES = [...manipulationRecipes, ...environmentRecipes];

export const getSceneRecipeOptions = () =>
  SCENE_RECIPES.filter((recipe) => !recipe.hideFromAssetLibrary).map((recipe) => ({
    value: recipe.id,
    label: recipe.label,
    ...getRecipeUiMetadata(recipe),
  }));

export const loadSceneRecipeById = async (recipeId, options = {}) => {
  const recipe = SCENE_RECIPES.find((entry) => entry.id === recipeId);
  if (!recipe) {
    throw new Error(`Unknown scene recipe: ${recipeId}`);
  }
  await loadRecipe(recipe, options);
};

export const loadPickAndPlaceSceneDemo = async (options = {}) => {
  const baseRecipe = SCENE_RECIPES.find((entry) => entry.id === "pick_and_place_ur5e");
  if (!baseRecipe) {
    throw new Error("Missing base recipe for UR5e pick-and-place demo.");
  }

  const trayAssetKey = getLibraryAssetKey(trayAsset);
  await loadRecipe(
    {
      ...baseRecipe,
      id: "pick_and_place_ur5e_demo",
      label: "Pick and Place (UR5e Demo)",
      steps: (baseRecipe.steps || []).filter((step) => step.assetKey !== trayAssetKey),
    },
    options
  );
};

export const loadPandaPickAndPlaceSceneDemo = async (options = {}) => {
  await loadRecipe(
    {
      id: "pick_and_place_panda_demo",
      label: "Pick and Place (Panda Demo)",
      kind: "libraryRecipe",
      steps: [
        {
          assetKey: getLibraryAssetKey(pandaAsset),
          applyKeyframe: "home",
        },
        {
          assetKey: getLibraryAssetKey(cubeAsset),
          transform: { position: [0.5903031005859375, -1.654568409364954e-17, 0.02548486328125] },
        },
      ],
    },
    options
  );
};

export const loadTrossenPickAndPlaceSceneDemo = async (options = {}) => {
  await loadRecipe(
    {
      id: "pick_and_place_trossen_demo",
      label: "Pick and Place (Trossen Demo)",
      kind: "libraryRecipe",
      cameraPreset: "trossenPickAndPlace",
      steps: [
        {
          assetKey: getLibraryAssetKey(trossenAsset),
          applyKeyframe: "home",
        },
        {
          assetKey: getLibraryAssetKey(cubeAsset),
          transform: { position: [0.275, 0.129, 0.025] },
        },
      ],
    },
    options
  );
};
